import axios from "axios";
import React, { useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import Card from "../Card";
import { useForm } from "react-hook-form";
import { BASE_URL } from "..";
import { SHOP_GET_API } from "../app/constants";
import useHomeStore from "./ZustandStore/useHomeStore";
import DynamicMetaTags from "../helper/DynamicMetaTags";

const CategoryProducts = () => {
  const { categorySlug } = useParams();
  const location = useLocation();

  const [active, setActive] = useState(false);
  const sidebarController = () => {
    setActive(!active);
  };
  const [products, setProducts] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [totalResults, setTotalResults] = useState(0);
  const [grid, setGrid] = useState(true);

  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [categoryData, setCategoryData] = useState(null);
  const pageSize = 12;

  const slug = location.pathname.split("/").slice(-1)[0];

  const fetchCategoryDetails = async (params) => {
    try {
      const response = await axios.get(
        `${BASE_URL}/public/category/products/inventory`,
        {
          params: {
            categorySlug: slug,
            size: params.size,
            page: params.page,
            minPrice: params.minPrice,
            maxPrice: params.maxPrice,
          },
        }
      );
      if (response.status === 200 && response.data.code === 1) {
        const metaTags = response.data.data;
        useHomeStore.getState().setMetaTags(metaTags);
        setProducts(response.data.data.productResponse.content);
        setCategoryData(response.data.data);
        setTotalPages(response.data.data.productResponse.totalPages);
        setTotalResults(response.data.data.productResponse.totalElements);
      } else {
        setError("Failed to fetch category data");
      }
    } catch (err) {
      setError("Error fetching category data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const params = {
      categorySlug,
      size: pageSize,
      page: currentPage,
      minPrice,
      maxPrice,
    };
    fetchCategoryDetails(params);
  }, [categorySlug, currentPage, minPrice, maxPrice, slug]);

  const renderPagination = () => {
    const pages = [];
    const startPage = Math.max(0, currentPage - 4);
    const endPage = Math.min(totalPages - 1, startPage + 9);

    pages.push(
      <li
        key="first"
        className={`page-item ${currentPage === 0 ? "disabled" : ""}`}
      >
        <Link className="page-link" to="#" onClick={() => setCurrentPage(0)}>
          First
        </Link>
      </li>
    );

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <li
          key={i}
          className={`page-item ${i === currentPage ? "active" : ""}`}
        >
          <Link className="page-link" to="#" onClick={() => setCurrentPage(i)}>
            {String(i + 1).padStart(2, "0")}
          </Link>
        </li>
      );
    }

    pages.push(
      <li
        key="last"
        className={`page-item ${
          currentPage === totalPages - 1 ? "disabled" : ""
        }`}
      >
        <Link
          className="page-link"
          to="#"
          onClick={() => setCurrentPage(totalPages - 1)}
        >
          Last
        </Link>
      </li>
    );

    return pages;
  };

  const [sortedProducts, setSortedProducts] = useState([]);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await axios.get(
          `${BASE_URL}${SHOP_GET_API}?page=${currentPage}` // Size is now handled by backend
        );
        setProducts(response.data.data.content);
        setTotalPages(response.data.data.totalPages);
        setTotalResults(response.data.data.totalElements);
      } catch (error) {
        console.error("Error fetching products:", error);
      }
    };
    fetchProducts();
  }, [currentPage]);

  useEffect(() => {
    setSortedProducts(products);
  }, [products]);

  const getPaginationRange = () => {
    const start = currentPage * pageSize + 1;
    const end = Math.min((currentPage + 1) * pageSize, totalResults);
    return `${start}-${end}`;
  };

  const { register, handleSubmit, watch } = useForm();
  const handleSortChange = (e) => {
    const selectedOption = e.target.value; // Get selected sorting option
    const params = new URLSearchParams();

    params.append("page", currentPage);
    if (selectedOption === "Latest") {
      params.append("sortBy", "Latest");
    } else if (selectedOption === "relevent") {
      params.append("sortBy", "relevent");
    } else if (selectedOption === "popular") {
      params.append("sortBy", "popularity");
    } else if (selectedOption === "price-asc") {
      params.append("sortBy", "priceLowToHigh");
    } else if (selectedOption === "price-desc") {
      params.append("sortBy", "priceHighToLow");
    }

    if (minPrice) params.append("minPrice", minPrice);
    if (maxPrice) params.append("maxPrice", maxPrice);

    axios
      .get(`${BASE_URL}${SHOP_GET_API}?${params.toString()}`)
      .then((response) => {
        setProducts(response.data.data.content);
        setTotalPages(response.data.data.totalPages);
        setTotalResults(response.data.data.totalElements);
      })
      .catch((error) => {
        console.error("Error fetching filtered products:", error);
      });
  };

  const handleFilter = (data) => {
    // Prepare params for filters
    const params = new URLSearchParams();

    // Apply price filters
    if (data.minPrice) params.append("minPrice", data.minPrice);
    if (data.maxPrice) params.append("maxPrice", data.maxPrice);

    // Fetch filtered data from API
    axios
      .get(`${BASE_URL}${SHOP_GET_API}?${params.toString()}`)
      .then((response) => {
        setProducts(response.data.data.content);
        setTotalPages(response.data.data.totalPages);
        setTotalResults(response.data.data.totalElements);
      })
      .catch((error) => {
        console.error("Error fetching filtered products:", error);
      });
  };

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  if (loading) {
    return <div>Loading...</div>;
  }

  if (error) {
    return <div>{error}</div>;
  }

  return (
    <section className="shop py-20">
      <DynamicMetaTags />
      <div className={`side-overlay ${active && "show"}`}></div>
      <div className="container container-lg">
        <div className="row">
          <div className="section-heading flex-between">
            <div className="flex-between flex-wrap gap-8">
              <h2 className="text-xl">{categoryData.title}</h2>
            </div>
            <div className="flex-between ga flex-wrap ">
              <span className="text-gray-900"></span>
              <div className="position-relative flex-align ga flex-wrap">
                {/* <div className=" flex-align ga">
                  <button
                    onClick={() => setGrid(false)} // Set to list view
                    type="button"
                    className={`w-44 h-44 flex-center border rounded-6 text-2xl list-btn border-gray-100 ${
                      !grid && "border-main-600 text-white bg-main-600"
                    }`}
                  >
                    <i className="ph-bold ph-list-dashes" />
                  </button>
                  <button
                    onClick={() => setGrid(true)} // Set to grid view
                    type="button"
                    className={`w-44 h-44 flex-center border rounded-6 text-2xl list-btn border-gray-100 ${
                      grid && "border-main-600 text-white bg-main-600"
                    }`}
                  >
                    <i className="ph ph-squares-four" />
                  </button>
                </div> */}
                <div className="position-relative text-gray-500 flex-align gap-4 text-14">
                  <label
                    htmlFor="sorting"
                    className="text-inherit flex-shrink-0"
                  >
                    Sort by:
                  </label>
                  <select
                    defaultValue={1}
                    className="form-control common-input px-14 py-14 text-inherit rounded-6 w-auto"
                    id="sorting"
                    onChange={handleSortChange}
                  >
                    <option value="latest">Latest</option>
                    <option value="relevent">Relevent</option>
                    <option value="popular">Popular</option>
                    <option value="price-asc">Price Low to High</option>
                    <option value="price-desc">Price High to Low</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
          <div className="col-lg-3">
            <div className={`shop-sidebar ${active && "active"}`}>
              <button
                onClick={sidebarController}
                type="button"
                className="shop-sidebar__close d-lg-none d-flex w-32 h-32 flex-center border border-gray-100 rounded-circle hover-bg-main-600 position-absolute inset-inline-end-0 me-10 mt-8  hover-text-white hover-border-main-600"
              >
                <i className="ph ph-x" />
              </button>
              <div className="shop-sidebar__box border border-gray-100 rounded-8 p-32 mt-20">
                <h6 className="text-xl border-bottom border-gray-100 pb-24 mb-24">
                  Product Category
                </h6>
                {/* max-h-540 overflow-y-auto scroll-sm */}
                <ul>
                  <li className="mb-24">
                    <Link
                      to="/product-details"
                      className="text-gray-900 hover-text-main-600"
                    >
                      Mobile &amp; Accessories (12)
                    </Link>
                  </li>
                  <li className="mb-24">
                    <Link
                      to="/product-details"
                      className="text-gray-900 hover-text-main-600"
                    >
                      Laptop (12)
                    </Link>
                  </li>
                  <li className="mb-24">
                    <Link
                      to="/product-details"
                      className="text-gray-900 hover-text-main-600"
                    >
                      Electronics (12)
                    </Link>
                  </li>
                  <li className="mb-24">
                    <Link
                      to="/product-details"
                      className="text-gray-900 hover-text-main-600"
                    >
                      Smart Watch (12)
                    </Link>
                  </li>
                  <li className="mb-24">
                    <Link
                      to="/product-details"
                      className="text-gray-900 hover-text-main-600"
                    >
                      Storage (12)
                    </Link>
                  </li>
                  <li className="mb-24">
                    <Link
                      to="/product-details"
                      className="text-gray-900 hover-text-main-600"
                    >
                      Portable Devices (12)
                    </Link>
                  </li>
                  <li className="mb-24">
                    <Link
                      to="/product-details"
                      className="text-gray-900 hover-text-main-600"
                    >
                      Action Camera (12)
                    </Link>
                  </li>
                  <li className="mb-24">
                    <Link
                      to="/product-details"
                      className="text-gray-900 hover-text-main-600"
                    >
                      Smart Gadget (12)
                    </Link>
                  </li>
                  <li className="mb-24">
                    <Link
                      to="/product-details"
                      className="text-gray-900 hover-text-main-600"
                    >
                      Monitor (12)
                    </Link>
                  </li>
                  <li className="mb-24">
                    <Link
                      to="/product-details"
                      className="text-gray-900 hover-text-main-600"
                    >
                      Smart TV (12)
                    </Link>
                  </li>
                  <li className="mb-24">
                    <Link
                      to="/product-details"
                      className="text-gray-900 hover-text-main-600"
                    >
                      Camera (12)
                    </Link>
                  </li>
                  <li className="mb-24">
                    <Link
                      to="/product-details"
                      className="text-gray-900 hover-text-main-600"
                    >
                      Monitor Stand (12)
                    </Link>
                  </li>
                  <li className="mb-0">
                    <Link
                      to="/product-details"
                      className="text-gray-900 hover-text-main-600"
                    >
                      Headphone (12)
                    </Link>
                  </li>
                </ul>
              </div>
              <div className="shop-sidebar__box border border-gray-100 rounded-8 p-32 mb-32">
                <h6 className="text-xl border-bottom border-gray-100 pb-24 mb-24">
                  Filter by Price
                </h6>

                <form onSubmit={handleSubmit(handleFilter)}>
                  <div className="custom--range">
                    <div className="flex-between gap-8 mt-24 flex-wrap">
                      <div className="d-flex gap-8">
                        <input
                          type="number"
                          className="form-control"
                          placeholder="Min"
                          {...register("minPrice")}
                          style={{
                            width: "100px",
                            height: "35px",
                            border: "1px solid #ccc",
                            borderRadius: "5px",
                            padding: "0 10px",
                          }}
                        />
                        <input
                          type="number"
                          className="form-control"
                          placeholder="Max"
                          {...register("maxPrice")}
                          style={{
                            width: "100px",
                            height: "35px",
                            border: "1px solid #ccc",
                            borderRadius: "5px",
                            padding: "0 10px",
                          }}
                        />
                      </div>
                      <button
                        type="submit"
                        className="btn btn-main h-40 flex-align"
                      >
                        <i className="bi bi-play-fill"></i>
                      </button>
                    </div>
                  </div>
                </form>
              </div>

              <div className="shop-sidebar__box border border-gray-100 rounded-8 p-32 mb-32">
                <h6 className="text-xl border-bottom border-gray-100 pb-24 mb-24">
                  Filter by Rating
                </h6>
                <div className="flex-align gap-8 position-relative mb-20">
                  <label
                    className="position-absolute w-100 h-100 cursor-pointer"
                    htmlFor="rating5"
                  >
                    {" "}
                  </label>
                  <div className="common-check common-radio mb-0">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="flexRadioDefault"
                      id="rating5"
                    />
                  </div>
                  <div
                    className="progress w-100 bg-gray-100 rounded-pill h-8"
                    role="progressbar"
                    aria-label="Basic example"
                    aria-valuenow={70}
                    aria-valuemin={0}
                    aria-valuemax={100}
                  >
                    <div
                      className="progress-bar bg-main-600 rounded-pill"
                      style={{ width: "70%" }}
                    />
                  </div>
                  <div className="flex-align gap-4">
                    <span className="text-xs fw-medium text-warning-600 d-flex">
                      <i className="ph-fill ph-star" />
                    </span>
                    <span className="text-xs fw-medium text-warning-600 d-flex">
                      <i className="ph-fill ph-star" />
                    </span>
                    <span className="text-xs fw-medium text-warning-600 d-flex">
                      <i className="ph-fill ph-star" />
                    </span>
                    <span className="text-xs fw-medium text-warning-600 d-flex">
                      <i className="ph-fill ph-star" />
                    </span>
                    <span className="text-xs fw-medium text-warning-600 d-flex">
                      <i className="ph-fill ph-star" />
                    </span>
                  </div>
                  <span className="text-gray-900 flex-shrink-0">124</span>
                </div>
                <div className="flex-align gap-8 position-relative mb-20">
                  <label
                    className="position-absolute w-100 h-100 cursor-pointer"
                    htmlFor="rating4"
                  >
                    {" "}
                  </label>
                  <div className="common-check common-radio mb-0">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="flexRadioDefault"
                      id="rating4"
                    />
                  </div>
                  <div
                    className="progress w-100 bg-gray-100 rounded-pill h-8"
                    role="progressbar"
                    aria-label="Basic example"
                    aria-valuenow={50}
                    aria-valuemin={0}
                    aria-valuemax={100}
                  >
                    <div
                      className="progress-bar bg-main-600 rounded-pill"
                      style={{ width: "50%" }}
                    />
                  </div>
                  <div className="flex-align gap-4">
                    <span className="text-xs fw-medium text-warning-600 d-flex">
                      <i className="ph-fill ph-star" />
                    </span>
                    <span className="text-xs fw-medium text-warning-600 d-flex">
                      <i className="ph-fill ph-star" />
                    </span>
                    <span className="text-xs fw-medium text-warning-600 d-flex">
                      <i className="ph-fill ph-star" />
                    </span>
                    <span className="text-xs fw-medium text-warning-600 d-flex">
                      <i className="ph-fill ph-star" />
                    </span>
                    <span className="text-xs fw-medium text-gray-400 d-flex">
                      <i className="ph-fill ph-star" />
                    </span>
                  </div>
                  <span className="text-gray-900 flex-shrink-0">52</span>
                </div>
                <div className="flex-align gap-8 position-relative mb-20">
                  <label
                    className="position-absolute w-100 h-100 cursor-pointer"
                    htmlFor="rating3"
                  >
                    {" "}
                  </label>
                  <div className="common-check common-radio mb-0">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="flexRadioDefault"
                      id="rating3"
                    />
                  </div>
                  <div
                    className="progress w-100 bg-gray-100 rounded-pill h-8"
                    role="progressbar"
                    aria-label="Basic example"
                    aria-valuenow={35}
                    aria-valuemin={0}
                    aria-valuemax={100}
                  >
                    <div
                      className="progress-bar bg-main-600 rounded-pill"
                      style={{ width: "35%" }}
                    />
                  </div>
                  <div className="flex-align gap-4">
                    <span className="text-xs fw-medium text-warning-600 d-flex">
                      <i className="ph-fill ph-star" />
                    </span>
                    <span className="text-xs fw-medium text-warning-600 d-flex">
                      <i className="ph-fill ph-star" />
                    </span>
                    <span className="text-xs fw-medium text-warning-600 d-flex">
                      <i className="ph-fill ph-star" />
                    </span>
                    <span className="text-xs fw-medium text-gray-400 d-flex">
                      <i className="ph-fill ph-star" />
                    </span>
                    <span className="text-xs fw-medium text-gray-400 d-flex">
                      <i className="ph-fill ph-star" />
                    </span>
                  </div>
                  <span className="text-gray-900 flex-shrink-0">12</span>
                </div>
                <div className="flex-align gap-8 position-relative mb-20">
                  <label
                    className="position-absolute w-100 h-100 cursor-pointer"
                    htmlFor="rating2"
                  >
                    {" "}
                  </label>
                  <div className="common-check common-radio mb-0">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="flexRadioDefault"
                      id="rating2"
                    />
                  </div>
                  <div
                    className="progress w-100 bg-gray-100 rounded-pill h-8"
                    role="progressbar"
                    aria-label="Basic example"
                    aria-valuenow={20}
                    aria-valuemin={0}
                    aria-valuemax={100}
                  >
                    <div
                      className="progress-bar bg-main-600 rounded-pill"
                      style={{ width: "20%" }}
                    />
                  </div>
                  <div className="flex-align gap-4">
                    <span className="text-xs fw-medium text-warning-600 d-flex">
                      <i className="ph-fill ph-star" />
                    </span>
                    <span className="text-xs fw-medium text-warning-600 d-flex">
                      <i className="ph-fill ph-star" />
                    </span>
                    <span className="text-xs fw-medium text-gray-400 d-flex">
                      <i className="ph-fill ph-star" />
                    </span>
                    <span className="text-xs fw-medium text-gray-400 d-flex">
                      <i className="ph-fill ph-star" />
                    </span>
                    <span className="text-xs fw-medium text-gray-400 d-flex">
                      <i className="ph-fill ph-star" />
                    </span>
                  </div>
                  <span className="text-gray-900 flex-shrink-0">5</span>
                </div>
                <div className="flex-align gap-8 position-relative mb-0">
                  <label
                    className="position-absolute w-100 h-100 cursor-pointer"
                    htmlFor="rating1"
                  >
                    {" "}
                  </label>
                  <div className="common-check common-radio mb-0">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="flexRadioDefault"
                      id="rating1"
                    />
                  </div>
                  <div
                    className="progress w-100 bg-gray-100 rounded-pill h-8"
                    role="progressbar"
                    aria-label="Basic example"
                    aria-valuenow={5}
                    aria-valuemin={0}
                    aria-valuemax={100}
                  >
                    <div
                      className="progress-bar bg-main-600 rounded-pill"
                      style={{ width: "5%" }}
                    />
                  </div>
                  <div className="flex-align gap-4">
                    <span className="text-xs fw-medium text-warning-600 d-flex">
                      <i className="ph-fill ph-star" />
                    </span>
                    <span className="text-xs fw-medium text-gray-400 d-flex">
                      <i className="ph-fill ph-star" />
                    </span>
                    <span className="text-xs fw-medium text-gray-400 d-flex">
                      <i className="ph-fill ph-star" />
                    </span>
                    <span className="text-xs fw-medium text-gray-400 d-flex">
                      <i className="ph-fill ph-star" />
                    </span>
                    <span className="text-xs fw-medium text-gray-400 d-flex">
                      <i className="ph-fill ph-star" />
                    </span>
                  </div>
                  <span className="text-gray-900 flex-shrink-0">2</span>
                </div>
              </div>
              <div className="shop-sidebar__box border border-gray-100 rounded-8 p-32 mb-32">
                <h6 className="text-xl border-bottom border-gray-100 pb-24 mb-24">
                  Filter by Color
                </h6>
                <ul className="max-h-540 overflow-y-auto scroll-sm">
                  <li className="mb-24">
                    <div className="form-check common-check common-radio checked-black">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="color"
                        id="color1"
                      />
                      <label className="form-check-label" htmlFor="color1">
                        Black(12)
                      </label>
                    </div>
                  </li>
                  <li className="mb-24">
                    <div className="form-check common-check common-radio checked-primary">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="color"
                        id="color2"
                      />
                      <label className="form-check-label" htmlFor="color2">
                        Blue (12)
                      </label>
                    </div>
                  </li>
                  <li className="mb-24">
                    <div className="form-check common-check common-radio checked-gray">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="color"
                        id="color3"
                      />
                      <label className="form-check-label" htmlFor="color3">
                        Gray (12)
                      </label>
                    </div>
                  </li>
                  <li className="mb-24">
                    <div className="form-check common-check common-radio checked-success">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="color"
                        id="color4"
                      />
                      <label className="form-check-label" htmlFor="color4">
                        Green (12)
                      </label>
                    </div>
                  </li>
                  <li className="mb-24">
                    <div className="form-check common-check common-radio checked-danger">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="color"
                        id="color5"
                      />
                      <label className="form-check-label" htmlFor="color5">
                        Red (12)
                      </label>
                    </div>
                  </li>
                  <li className="mb-24">
                    <div className="form-check common-check common-radio checked-white">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="color"
                        id="color6"
                      />
                      <label className="form-check-label" htmlFor="color6">
                        White (12)
                      </label>
                    </div>
                  </li>
                  <li className="mb-0">
                    <div className="form-check common-check common-radio checked-purple">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="color"
                        id="color7"
                      />
                      <label className="form-check-label" htmlFor="color7">
                        Purple (12)
                      </label>
                    </div>
                  </li>
                </ul>
              </div>
              <div className="shop-sidebar__box border border-gray-100 rounded-8 p-32 mb-32">
                <h6 className="text-xl border-bottom border-gray-100 pb-24 mb-24">
                  Filter by Brand
                </h6>
                <ul className="max-h-540 overflow-y-auto scroll-sm">
                  <li className="mb-24">
                    <div className="form-check common-check common-radio">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="color"
                        id="brand1"
                      />
                      <label className="form-check-label" htmlFor="brand1">
                        Apple
                      </label>
                    </div>
                  </li>
                  <li className="mb-24">
                    <div className="form-check common-check common-radio">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="color"
                        id="brand2"
                      />
                      <label className="form-check-label" htmlFor="brand2">
                        Samsung
                      </label>
                    </div>
                  </li>
                  <li className="mb-24">
                    <div className="form-check common-check common-radio">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="color"
                        id="brand3"
                      />
                      <label className="form-check-label" htmlFor="brand3">
                        Microsoft
                      </label>
                    </div>
                  </li>
                  <li className="mb-24">
                    <div className="form-check common-check common-radio">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="color"
                        id="brand4"
                      />
                      <label className="form-check-label" htmlFor="brand4">
                        Apple
                      </label>
                    </div>
                  </li>
                  <li className="mb-24">
                    <div className="form-check common-check common-radio">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="color"
                        id="brand5"
                      />
                      <label className="form-check-label" htmlFor="brand5">
                        HP
                      </label>
                    </div>
                  </li>
                  <li className="mb-24">
                    <div className="form-check common-check common-radio">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="color"
                        id="DELL"
                      />
                      <label className="form-check-label" htmlFor="DELL">
                        DELL
                      </label>
                    </div>
                  </li>
                  <li className="mb-0">
                    <div className="form-check common-check common-radio">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="color"
                        id="Redmi"
                      />
                      <label className="form-check-label" htmlFor="Redmi">
                        Redmi
                      </label>
                    </div>
                  </li>
                </ul>
              </div>
              <div className="shop-sidebar__box rounded-8">
                <img
                  className="rounded-top-4 w-100 overflow-hidden"
                  style={{ minWidth: "100%", maxHeight: "12rem" }}
                  src="assets/images/thumbs/advertise-img1.png"
                  alt=""
                />
              </div>
            </div>
          </div>
          <div className="col-lg">
            {/* Top Start */}
            <section className="shop-products">
              {/* Upper Section */}

              {/* Main Content */}
              <div className="container container-lg">
                <div className="flex-between ga flex-wrap">
                  {/* <h2 className="text-xl">{metaTags}</h2> */}
                  <h2 className="text-xl"></h2>
                  <div className="position-relative flex-align ga flex-wrap">
                    {/* <div className=" flex-align ga">
                      <button
                        onClick={() => setGrid(false)}
                        type="button"
                        className={`w-44 h-44 flex-center border rounded-6 text-2xl list-btn border-gray-100 ${
                          !grid && "border-main-600 text-white bg-main-600"
                        }`}
                      >
                        <i className="ph-bold ph-list-dashes" />
                      </button>
                      <button
                        onClick={() => setGrid(true)} // Set to grid view
                        type="button"
                        className={`w-44 h-44 flex-center border rounded-6 text-2xl list-btn border-gray-100 ${
                          grid && "border-main-600 text-white bg-main-600"
                        }`}
                      >
                        <i className="ph ph-squares-four" />
                      </button>
                    </div> */}
                  </div>
                </div>
                {/* Section Header */}
                <div className="section-heading">
                  <div className="flex-between flex-wrap gap-8"></div>
                </div>

                {/* Product Grid or List */}
                {grid ? (
                  <div
                    className="product-grid"
                    style={{
                      display: "grid",
                      alignItems: "center",
                      justifyContent: "center",
                      gridTemplateColumns:
                        "repeat(auto-fit, minmax(240px, 1fr))",
                      gap: "26px",
                    }}
                  >
                    {products.map((product) => (
                      <div key={product.productId}>
                        <Card
                          imageSrc={product.image}
                          title={product.title}
                          badge={product.badge}
                          offered={product.selling}
                          purchased={product.offered}
                          averageRating={product.averageRating}
                          reviewCount={product.reviewCount}
                          productViews={product.productViews}
                          soldOut={product.soldOut}
                          productLink={`/product/${product.slug}`}
                          cartID={product.productId}
                          freeDelivery={product.freeDelivery}
                          product={product}
                        />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="row">
                    {products.map((product) => (
                      <div className="col-12 mb-4" key={product.productId}>
                        <div className="table-product d-flex align-items-center gap-24">
                          <Link
                            to={`/product/${product.slug}`}
                            className="table-product__thumb rounded-8 flex-center"
                            aria-label={`View details of ${product.title}`}
                          >
                            <img
                              src={product.image}
                              alt={product.title}
                              className="img-fluid"
                            />
                          </Link>
                          <div className="table-product__content text-start">
                            <h6
                              className="title text-lg fw-semibold mb-8"
                              style={{ minWidth: "3rem" }}
                            >
                              <Link
                                to={`/product/${product.slug}`}
                                className="link text-line-2"
                                tabIndex={0}
                                aria-label={`View details of ${product.title}`}
                              >
                                {product.title}
                              </Link>
                            </h6>
                            {product.description && (
                              <h6 className="title text-sm fw-semibold mb-8 text-muted d-none d-md-block">
                                {product.description}
                              </h6>
                            )}
                            <div>
                              <span className="text-lg text-main h6 mb-0 text-nowrap">
                                Rs. {product.offered}
                              </span>
                              {product.selling && (
                                <div className="text-decoration-line-through text-sm mb-0 fw-semibold text-muted">
                                  Rs. {product.selling}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Pagination */}
                <ul className="pagination">{renderPagination()}</ul>
              </div>
            </section>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CategoryProducts;
